<script setup lang="ts">
import { PropertyId, PropertySchema } from "~/classes/entity"

const props = defineProps({
  files: null
})

const videoMetadata = computed(() => mapped(props.files, f => f.videoMetadata()))

function anyProperty(propertyId: PropertyId) {
  return any(Object.values(props.files), file => file.property(propertyId))
}

function anyVideoMetadataProperty(
  field: keyof FileVideoContainerMetadata,
) {
  return any(Object.values(videoMetadata.value), metadata => metadata?.[field])
}

function stringProperty(
  field: keyof FileVideoContainerMetadata,
): Record<string, EntityPropertyValue | undefined> {
  return mapped(videoMetadata.value, (metadata) => {
    if (metadata && field in metadata)
      return { type: PropertySchema.AnyStringValue, anyString: metadata[field] as any }
  })
}

function numberProperty(
  field: keyof FileVideoContainerMetadata,
): Record<string, EntityPropertyValue | undefined> {
  return mapped(videoMetadata.value, (metadata) => {
    if (metadata && field in metadata)
      return { type: PropertySchema.AnyIntegerValue, anyInteger: metadata[field] as any }
  })
}

function isSingleMultilineField(
  field: keyof FileVideoContainerMetadata,
) {
  if (len(videoMetadata.value) > 1)
    return false
  const value = first(videoMetadata.value)?.[field] ?? ""
  if (typeof value === "string")
    return len(value) > 24
  else return len(JSON.stringify(value)) > 24
}

const durations: Ref<Record<string, EntityPropertyValue | undefined>> = computed(() => mapped(props.files, (f) => {
  const durationMs = f.mediaDurationMs()
  if (isDefined(durationMs))
    return {
      type: PropertySchema.NonEmptyStringValue,
      nonEmptyString: msToHhMmSs(durationMs),
    }
}))
</script>

<template>
  <panel-details-property-section class="section">
    <template #header>
      Video Details
    </template>
    <panel-details-property-field
      v-if="anyProperty(PropertyId.MEDIA_DURATION_MS)"
      :values="durations"
      label="Duration"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('pixelFormat')"
      :multiline="isSingleMultilineField('pixelFormat')"
      :values="stringProperty('pixelFormat')"
      label="Pixel Format"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('frameRate')"
      :multiline="isSingleMultilineField('frameRate')"
      :values="numberProperty('frameRate')"
      label="Frame Rate"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('bitRate')"
      :multiline="isSingleMultilineField('bitRate')"
      :values="numberProperty('bitRate')"
      label="Bit Rate"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('colorSpace')"
      :multiline="isSingleMultilineField('colorSpace')"
      :values="stringProperty('colorSpace')"
      label="Color Space"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('colorRange')"
      :multiline="isSingleMultilineField('colorRange')"
      :values="stringProperty('colorRange')"
      label="Color Range"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('colorPrimaries')"
      :multiline="isSingleMultilineField('colorPrimaries')"
      :values="stringProperty('colorPrimaries')"
      label="Color Primaries"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('colorTransferCharacteristic')"
      :multiline="isSingleMultilineField('colorTransferCharacteristic')"
      :values="stringProperty('colorTransferCharacteristic')"
      label="Color Transfer Characteristic"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('chromaLocation')"
      :multiline="isSingleMultilineField('chromaLocation')"
      :values="stringProperty('chromaLocation')"
      label="Chroma Location"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('aspectRatio')"
      :multiline="isSingleMultilineField('aspectRatio')"
      :values="stringProperty('aspectRatio')"
      label="Aspect Ratio"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('codecName')"
      :multiline="isSingleMultilineField('codecName')"
      :values="stringProperty('codecName')"
      label="Codec Name"
    />
    <panel-details-property-field
      v-if="anyVideoMetadataProperty('codecDescription')"
      :multiline="isSingleMultilineField('codecDescription')"
      :values="stringProperty('codecDescription')"
      label="Codec Description"
    />
  </panel-details-property-section>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'
</style>
